import * as React from "react";
import Box from "@mui/material/Box";
import { Typography, Select, MenuItem } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import moment from "moment";
import { MultiFieldContainer } from "./InvoicesTab.styled";
import { getCurrence } from "../../../constants/TablesColumns";

// Helper to build the standard Invoices columns used across screens.
// Consumers can append their own action columns (email/print) or inject extra columns as needed.
export const buildInvoicesBaseColumns = ({
  handleCloseStatus,
  filteredStatuses = [],
  invoicesStatuses = [],
  onRedirect,
  readOnlyStatus = false, // if true, render status as a pill only (no Select)
  hideStatusCaret = false, // if true (and not readOnlyStatus), hide dropdown arrow
} = {}) => {

  const renderStatusPill = (raw) => {
    if (!raw) return (
      <Box component="span" sx={{ fontSize: 12, color: 'gray', ml: 1.5 }}>N/A</Box>
    );
    const label = typeof raw === 'number' ? (invoicesStatuses?.find(s => s.id === raw)?.name ?? raw) : raw;
    const l = String(label).toLowerCase();
    let bg = "grey.300"; let fg = "text.primary";
    if (l === "paid - pending review") { bg = "#e0f2fe"; fg = "#075985"; }
    else if (l === "paid") { bg = "#dff6dd"; fg = "#1e4620"; }
    else if (l === "overdue") { bg = "#fed9cc"; fg = "#d83b01"; }
    else if (l === "on hold") { bg = "#fff4e5"; fg = "#8a5a14"; }
    else if (l === "unpaid") { bg = "#e5e7eb"; fg = "#374151"; }
    else if (l === "void") { bg = "#f3f4f6"; fg = "#6b7280"; }
    return (
      <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', px: 1,
        borderRadius: '15px', fontSize: 12, fontWeight: 600, bgcolor: bg, color: fg,
        textTransform: 'capitalize', minWidth: 60, height: 22, lineHeight: 1, textAlign: 'center', ml: 1.5
      }}>{label}</Box>
    );
  };

  const statusColumn = readOnlyStatus ? {
    field: 'status',
    headerName: 'Status',
    width: 125,
    sortable: true,
    renderCell: (params) => renderStatusPill(params.row.status)
  } : {
    field: 'status',
    headerName: 'Status',
    width: 125,
    editable: true,
    renderCell: (params) => (
      <MultiFieldContainer>
        <Select
          value={params.row.status || ''}
          onChange={(event) => handleCloseStatus && handleCloseStatus(params.row.id, event.target.value)}
          variant="outlined"
          displayEmpty
          IconComponent={hideStatusCaret ? () => null : undefined}
          renderValue={(value) => {
            if (!value) return <Typography color="gray">Choose Invoice Status</Typography>;
            return renderStatusPill(value);
          }}
          style={{ height: '30px', marginLeft: '15px' }}
          sx={hideStatusCaret ? { '.MuiSelect-icon': { display: 'none' }, pr: 0.5 } : undefined}
        >
          {filteredStatuses.map(s => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </Select>
      </MultiFieldContainer>
    )
  };

  // Email status pill renderer (same pill styling, with small leading icon)
  const renderEmailStatusPill = (raw) => {
    if (!raw) return <Box component="span" sx={{ fontSize: 12, color: 'gray', ml: 1.5 }}>N/A</Box>;
    const label = String(raw);
    const l = label.toLowerCase();
    let bg = '#e5e7eb';
    let fg = '#374151';
    let Icon = null;
    if (l === 'sent') { bg = '#dff6dd'; fg = '#1e4620'; Icon = CheckCircleOutlineIcon; }
    else if (l === 'failed') { bg = '#fed9cc'; fg = '#d83b01'; Icon = ErrorOutlineIcon; }
    else if (l === 'pending') { bg = '#fff4e5'; fg = '#8a5a14'; Icon = HourglassEmptyIcon; }
    else if (l === 'ready for send') { bg = '#e0f2fe'; fg = '#075985'; Icon = SendOutlinedIcon; }
    return (
      <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', px: 1, borderRadius: '15px', fontSize: 12,
        fontWeight: 600, bgcolor: bg, color: fg, textTransform: 'capitalize', minWidth: 60,
        height: 22, lineHeight: 1, ml: 1.5
      }}>
        {Icon && <Icon sx={{ fontSize: 14, mr: 0.5 }} />}{label}
      </Box>
    );
  };

  return [
    { field: 'naicCode', headerName: 'NAIC#', width: 75, editable: true },
    {
      field: 'memberName',
      headerName: 'Member Name',
      width: 100,
      minWidth: 100,
      editable: true,
      flex: 1,
      renderCell: (params) => {
        const isSpecial = params?.row?.naicCode === ' 998000';
        return (
          <Box sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            px: 0.5,
            bgcolor: isSpecial ? '#f3f4f6' : 'transparent',
          }}>
            <Box sx={{ whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{params.value}</Box>
          </Box>
        );
      }
    },
    { field: 'assessmentTitle', headerName: 'Assessment Title', width: 200, minWidth: 150, flex: 1, renderCell: (params) => {
      const isSpecial = params?.row?.naicCode === ' 998000';
      const value = params.row?.assessmentTitle || params.row?.assessmentType || '';
      return (
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          px: 0.5,
          bgcolor: isSpecial ? '#f3f4f6' : 'transparent',
        }}>
          <Box sx={{ whiteSpace: 'normal', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{value}</Box>
        </Box>
      );
    } },
    { field: 'invoiceDate', headerName: 'Date', width: 100, minWidth: 75, editable: true, valueGetter: (value) => value ? moment.utc(value).local().format('MM/DD/YYYY') : 'N/A' },
    { field: 'period', headerName: 'Period', width: 150, editable: true },
    { field: 'assessmentType', headerName: 'Assessment Type', minWidth: 100, width: 150, editable: true },
    { field: 'noticeNumber', headerName: 'Invoice number', width: 75, editable: true },
    statusColumn,
    { field: 'amount', headerName: 'Amount', width: 75, editable: true, valueGetter: getCurrence, align: 'right', headerAlign: 'left' },
    { field: 'amountPaid', headerName: 'Paid', width: 100, editable: true, align: 'right', headerAlign: 'left', renderCell: (params) => (
      <Typography className="link" sx={{ width: '100%', textAlign: 'left' }} onClick={() => onRedirect && onRedirect('payments', params?.row?.invoiceNumber)}>
        {getCurrence(params.value)}
      </Typography>
    ) },
  { field: 'emailStatus', headerName: 'Email Status', width: 140, sortable: true, renderCell: (params) => renderEmailStatusPill(params.row.emailStatus) },
  ];
};
